// src/components/ImpositionSVG.js
// Preview gráfico de la imposición (forma real del DXF: cut rojo / crease ámbar) sobre la hoja.
// Reutilizado por el cotizador de 1 pieza (QuoteView) y el multipieza (ProductQuoteView / spec sheet).
// El servidor es la fuente de la geometría (`geo`); las polilíneas (`polys`) se parsean del DXF en cliente.
import React from 'react';
import { bbox as dxfBbox, toPoints } from '../lib/dxfParse';

export const STROKE = (c) => (c === 2 ? '#d97706' : '#dc2626');           // crease ámbar / cut rojo
export const rot90 = (pts, blankX) => pts.map(([x, y]) => [y, blankX - x]); // 90° → bbox blankY×blankX

const ImpositionSVG = ({ geo, polys, maxHeight = '14rem' }) => {
  if (!geo || !geo.sheetW || !geo.sheetH || !polys || !polys.length) return null;
  const { sheetW, sheetH, nx, ny, deg, blankX, blankY, gap = 0, mode } = geo;
  const sw = Math.max(sheetW, sheetH) / 320;
  const bb = dxfBbox(polys);
  const base = polys.map((p) => ({ color: p.color, pts: p.pts.map(([x, y]) => [x - bb.minx, y - bb.miny]) }));
  const shapes = [];

  if (mode === 'nesting') {
    // Rota 90° la impo si no entra a lo ancho de la hoja pero sí rotada → marco y piezas consistentes.
    let pts0 = base; let fw = bb.x; let fh = bb.y;
    if (bb.x > sheetW + 0.05 && bb.y <= sheetW + 0.05) {
      pts0 = base.map((p) => ({ color: p.color, pts: rot90(p.pts, bb.x) }));
      fw = bb.y; fh = bb.x;
    }
    const ox = Math.max(0, (sheetW - fw) / 2); const oy = Math.max(0, (sheetH - fh) / 2);
    pts0.forEach((p, i) => shapes.push(
      <polyline key={i} points={toPoints(p.pts, ox, oy, sheetH)} fill="none" stroke={STROKE(p.color)} strokeWidth={sw} />));
  } else {
    const cell = (deg === 90) ? base.map((p) => ({ color: p.color, pts: rot90(p.pts, bb.x) })) : base;
    const bw = (deg === 90) ? blankY : blankX;
    const bh = (deg === 90) ? blankX : blankY;
    for (let r = 0; r < (ny || 1); r++) {
      for (let c = 0; c < (nx || 1); c++) {
        const ox = c * (bw + gap); const oy = r * (bh + gap);
        if (ox + bw > sheetW + 0.05 || oy + bh > sheetH + 0.05) continue;
        cell.forEach((p, i) => shapes.push(
          <polyline key={`${r}-${c}-${i}`} points={toPoints(p.pts, ox, oy, sheetH)} fill="none" stroke={STROKE(p.color)} strokeWidth={sw * 0.85} />));
      }
    }
  }
  // ── Sentido de FLAUTA (corrugado): ondas + flecha sobre la hoja, para validar visualmente ──
  // El motor expone geo.hasFlute / geo.fluteDir ('x' = la flauta corre a lo ancho de la hoja).
  const fluteShapes = [];
  if (geo.hasFlute && (geo.fluteDir === 'x' || geo.fluteDir === 'y')) {
    const horiz = geo.fluteDir === 'x';                 // ondas paralelas al eje de la flauta
    const span = horiz ? sheetW : sheetH;
    const cross = horiz ? sheetH : sheetW;
    const amp = cross / 70;
    const wl = span / 10;
    const lines = 9;
    for (let i = 1; i < lines; i++) {
      const c0 = (cross * i) / lines;                   // posición transversal de cada onda
      let pts = '';
      for (let t = 0; t <= span; t += span / 64) {
        const w = c0 + amp * Math.sin((t / wl) * 2 * Math.PI);
        pts += horiz ? `${t.toFixed(2)},${w.toFixed(2)} ` : `${w.toFixed(2)},${t.toFixed(2)} `;
      }
      fluteShapes.push(<polyline key={`fl-${i}`} points={pts} fill="none" stroke="#3b82f6" strokeWidth={sw * 0.5} opacity="0.45" />);
    }
    // Flecha de dirección + rótulo del flute.
    const my = horiz ? sheetH / 2 : sheetW * 0.06;       // posición de la flecha
    const a1 = span * 0.30; const a2 = span * 0.70; const ah = amp * 2.2;
    const line = horiz
      ? `M ${a1} ${my} L ${a2} ${my} M ${a2 - ah} ${my - ah} L ${a2} ${my} L ${a2 - ah} ${my + ah}`
      : `M ${my} ${a1} L ${my} ${a2} M ${my - ah} ${a2 - ah} L ${my} ${a2} L ${my + ah} ${a2 - ah}`;
    fluteShapes.push(<path key="fl-arrow" d={line} fill="none" stroke="#1d4ed8" strokeWidth={sw * 1.1} />);
    fluteShapes.push(
      <text key="fl-label" x={horiz ? span * 0.31 : my + amp * 3} y={(horiz ? my : a1) - amp}
        fontSize={cross / 16} fill="#1d4ed8" fontWeight="700">{`Flauta ${geo.flute || ''}`}</text>);
  }

  return (
    <svg viewBox={`0 0 ${sheetW} ${sheetH}`} preserveAspectRatio="xMidYMid meet"
      className="w-full border rounded bg-gray-50" style={{ maxHeight }}>
      <rect x="0" y="0" width={sheetW} height={sheetH} fill="#f8fafc" stroke="#94a3b8" strokeWidth={sw} />
      {fluteShapes}
      {shapes}
    </svg>
  );
};

export default ImpositionSVG;
